
import { Order, Driver, SystemConfig, DeliveryProvider, OperatingContext, SmartSplitSuggestion } from '../../../types';
import { isScheduledRoutingEligible } from '../core/scheduled';

/**
 * S.A.L. QUEUE REORGANIZATION v4.1.3 (Strict State Separation)
 * Organiza a visualização lateral da triagem com base nas decisões do Cérebro Logístico.
 * FIX: Inclui status 'payment_pending' e 'TAKEOUT' na piscina de triagem.
 * Apenas rotas (smartClusterId) são projetadas visualmente como sugestão.
 */
export const reorganizeQueueSmartly = (
    orders: Order[],
    _hubLat: number, _hubLng: number,
    _maxRadius: number, _maxAngle: number,
    _minDistanceForAngle: number = 1.0,
    _maxOrdersPerRoute: number = 4,
    _slaToleranceMinutes: number = 10,
    _averageKitchenTime: number = 20,
    _maxTimeForAngleIgnore: number = 15,
    _systemConfig: SystemConfig,
    _drivers: Driver[],
    _providers: DeliveryProvider[],
    _context: OperatingContext,
    brainSuggestion?: SmartSplitSuggestion | null
): Order[] => {
    // 1. Separação de Pedidos já despachados ou finalizados (Histórico/Rua)
    const assignedOrDispatched = orders.filter(o => o.status === 'assigned' || o.status === 'dispatched' || o.status === 'dispatched_3rd_party');
    const history = orders.filter(o => ['delivered', 'archived', 'returned', 'canceled'].includes(o.status));

    // 2. Piscina de Triagem (Tudo que está no Hub e ainda não saiu)
    const triageStatuses = ['pending', 'payment_pending', 'waiting_confirmation', 'scheduled', 'scheduled_confirmed', 'confirmed', 'ready'];
    const triagePool = orders.filter(o => triageStatuses.includes(o.status));

    if (!brainSuggestion) {
        // Se não houver sugestão, apenas garante que a triagem venha primeiro
        return [...triagePool, ...assignedOrDispatched, ...history];
    }

    // Cores para os clusters visuais
    const CLUSTER_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6', '#06b6d4'];

    // Mapeia metadados de agrupamento nos pedidos da triagem
    const enrichedTriage = triagePool.map(order => {
        // v56.9: PEDIDOS DE BALCÃO (TAKEOUT) NÃO ENTRAM EM ROTAS
        // Eles devem ser ignorados pela lógica de cluster para manterem sua posição ou serem tratados separadamente.
        if (order.type === 'TAKEOUT') return order;

        const routeIdx = brainSuggestion.routes.findIndex(r => r.orders.some(o => o.id === order.id));
        const route = routeIdx !== -1 ? brainSuggestion.routes[routeIdx] : null;

        // Verifica se o cérebro SUGESTAO reter este pedido
        const isSuggestedHold = !order.isManuallyAdjusted && brainSuggestion.heldOrders?.some(ho => ho.id === order.id);

        if (route || isSuggestedHold) {
            const seq = route ? route.orders.findIndex(o => o.id === order.id) + 1 : undefined;
            return {
                ...order,
                smartClusterId: route ? `cluster_${routeIdx}` : undefined,
                clusterColor: route ? (route.type === 'own' ? CLUSTER_COLORS[routeIdx % CLUSTER_COLORS.length] : '#f97316') : undefined,
                routeSequence: seq,
                optimizationOutcome: route?.type,
                isHeldForBatching: order.isHeldForBatching
            };
        }
        return order;
    });

    // Ordenação da Fila de Triagem Lateral:
    // 1. Prioriza rotas sugeridas pelo cérebro (agrupados aparecem primeiro)
    // 2. Mantém a sequência de entrega (1, 2, 3...) dentro do grupo
    // 3. Pedidos sem rota ficam por ordem de chegada
    const scheduledLeadMinutes = _systemConfig?.cardapioWeb?.scheduledRoutingLeadMinutes ?? 45;
    const sortedTriage = enrichedTriage.sort((a, b) => {
        const aScheduledLocked = !isScheduledRoutingEligible(a, scheduledLeadMinutes);
        const bScheduledLocked = !isScheduledRoutingEligible(b, scheduledLeadMinutes);
        if (aScheduledLocked !== bScheduledLocked) return aScheduledLocked ? 1 : -1;

        if (a.smartClusterId && b.smartClusterId) {
            if (a.smartClusterId === b.smartClusterId) {
                return (a.routeSequence || 0) - (b.routeSequence || 0);
            }
            return a.smartClusterId.localeCompare(b.smartClusterId);
        }
        if (a.smartClusterId) return -1;
        if (b.smartClusterId) return 1;
        return (a.createdAt || 0) - (b.createdAt || 0);
    });

    // Retorna a fila organizada: Triagem Ativa -> Em Rota -> Histórico
    return [...sortedTriage, ...assignedOrDispatched, ...history];
};
